import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { interactionsAPI } from '../../services/api';
import { Button, Input, Select, Modal } from '../ui';

const INTERACTION_TYPES = [
  { value: 'call', label: 'Phone Call', icon: '📞' },
  { value: 'email', label: 'Email', icon: '✉️' },
  { value: 'sms', label: 'Text Message', icon: '💬' },
  { value: 'meeting', label: 'Meeting', icon: '📅' },
  { value: 'note', label: 'Note', icon: '📝' },
];

const SMS_CHANNELS = [
  { value: 'apple_messages', label: 'Apple Messages' },
  { value: 'google_messages', label: 'Google Messages' },
  { value: 'google_voice', label: 'Google Voice' },
  { value: 'manual', label: 'Other' },
];

const EMAIL_CHANNELS = [
  { value: 'gmail', label: 'Gmail' },
  { value: 'outlook', label: 'Outlook' },
  { value: 'manual', label: 'Other' },
];

const DIRECTIONS = [
  { value: 'outbound', label: 'Outbound (you reached out)' },
  { value: 'inbound', label: 'Inbound (they reached out)' },
];

const CALL_OUTCOMES = [
  { value: 'connected', label: 'Connected - had conversation' },
  { value: 'voicemail', label: 'Left voicemail' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'busy', label: 'Busy / call back later' },
  { value: 'wrong_number', label: 'Wrong number' },
];

interface LogInteractionModalProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
  contactName: string;
  interaction?: any; // Optional - if provided, modal is in edit mode
}

interface InteractionFormData {
  type: string;
  direction: string;
  channel: string;
  subject: string;
  content: string;
  occurred_at: string;
  // Call-specific
  call_duration_minutes: string;
  call_outcome: string;
  // Meeting-specific
  meeting_date: string;
  meeting_start_time: string;
  meeting_end_time: string;
  meeting_location: string;
}

// Get current datetime in local timezone for datetime-local input
function getLocalDateTimeString(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

// Get current date in YYYY-MM-DD format
function getLocalDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

const initialFormData: InteractionFormData = {
  type: 'call',
  direction: 'outbound',
  channel: 'manual',
  subject: '',
  content: '',
  occurred_at: getLocalDateTimeString(),
  call_duration_minutes: '',
  call_outcome: 'connected',
  meeting_date: getLocalDateString(),
  meeting_start_time: '10:00',
  meeting_end_time: '11:00',
  meeting_location: '',
};

export function LogInteractionModal({ open, onClose, contactId, contactName, interaction }: LogInteractionModalProps) {
  const queryClient = useQueryClient();
  const isEditMode = !!interaction;

  const [formData, setFormData] = useState<InteractionFormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);

  // Reset or populate form when modal opens
  useEffect(() => {
    if (open) {
      if (interaction) {
        // Edit mode - populate from existing interaction
        const occurredDate = new Date(interaction.occurred_at * 1000);
        setFormData({
          type: interaction.type || 'call',
          direction: interaction.direction || 'outbound',
          channel: interaction.channel || 'manual',
          subject: interaction.subject || '',
          content: interaction.body || '',
          occurred_at: occurredDate.toISOString().slice(0, 16),
          call_duration_minutes: interaction.call_duration_seconds ? String(Math.floor(interaction.call_duration_seconds / 60)) : '',
          call_outcome: interaction.metadata?.outcome || 'connected',
          meeting_date: interaction.meeting_start_at ? new Date(interaction.meeting_start_at * 1000).toISOString().slice(0, 10) : getLocalDateString(),
          meeting_start_time: interaction.meeting_start_at ? new Date(interaction.meeting_start_at * 1000).toISOString().slice(11, 16) : '10:00',
          meeting_end_time: interaction.meeting_end_at ? new Date(interaction.meeting_end_at * 1000).toISOString().slice(11, 16) : '11:00',
          meeting_location: interaction.meeting_location || '',
        });
      } else {
        // Create mode - reset to defaults
        setFormData({
          ...initialFormData,
          occurred_at: getLocalDateTimeString(),
          meeting_date: getLocalDateString(),
        });
      }
      setError(null);
    }
  }, [open, interaction]);

  const mutation = useMutation({
    mutationFn: (data: InteractionFormData) => {
      // Build the interaction payload based on type
      const payload: Record<string, unknown> = {
        type: data.type,
        direction: data.direction,
        subject: data.subject || undefined,
        body: data.content || undefined,
      };

      // Only add contact_id for create mode (not allowed in update)
      if (!isEditMode) {
        payload.contact_id = contactId;
      }

      // Set channel based on type
      if (data.type === 'sms') {
        payload.channel = data.channel;
      } else if (data.type === 'email') {
        payload.channel = data.channel;
      } else {
        payload.channel = 'manual';
      }

      // Handle occurred_at based on type
      if (data.type === 'meeting') {
        // For meetings, use the meeting start time
        const startDateTime = new Date(`${data.meeting_date}T${data.meeting_start_time}`);
        const endDateTime = new Date(`${data.meeting_date}T${data.meeting_end_time}`);
        payload.occurred_at = Math.floor(startDateTime.getTime() / 1000);
        payload.meeting_start_at = Math.floor(startDateTime.getTime() / 1000);
        payload.meeting_end_at = Math.floor(endDateTime.getTime() / 1000);
        payload.meeting_location = data.meeting_location || undefined;
      } else {
        payload.occurred_at = Math.floor(new Date(data.occurred_at).getTime() / 1000);
      }

      // Add call duration if provided
      if (data.type === 'call' && data.call_duration_minutes) {
        payload.call_duration_seconds = parseInt(data.call_duration_minutes) * 60;
      }

      // Store call outcome in metadata
      if (data.type === 'call') {
        payload.metadata = { outcome: data.call_outcome };
        // If not connected, clear the body
        if (data.call_outcome !== 'connected' && !data.content) {
          payload.body = `Call outcome: ${CALL_OUTCOMES.find(o => o.value === data.call_outcome)?.label || data.call_outcome}`;
        }
      }

      // Use update or create based on mode
      if (isEditMode && interaction) {
        return interactionsAPI.update(interaction.id, payload);
      } else {
        return interactionsAPI.create(payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
      queryClient.invalidateQueries({ queryKey: ['interactions', contactId] });
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to log interaction');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate(formData);
  };

  const handleChange = (field: keyof InteractionFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const getPlaceholder = () => {
    switch (formData.type) {
      case 'call':
        return formData.call_outcome === 'connected'
          ? 'What did you discuss? Key points, next steps, etc.'
          : 'Any additional notes about the call attempt...';
      case 'email':
        return 'Key points from the email conversation...';
      case 'sms':
        return `Paste your text conversation here...

Example format:
Me: Hey! Following up on the property at 123 Main St
${contactName.split(' ')[0]}: Yes! We're still interested
Me: Great, when works for a showing?
${contactName.split(' ')[0]}: How about Saturday at 2pm?`;
      case 'meeting':
        return 'Meeting notes: what was discussed, decisions made, action items...';
      case 'note':
        return 'Your internal notes about this contact...';
      default:
        return 'Details...';
    }
  };

  const getSubjectPlaceholder = () => {
    switch (formData.type) {
      case 'call':
        return 'Brief summary (e.g., "Discussed showing schedule")';
      case 'email':
        return 'Email subject or summary';
      case 'sms':
        return 'Conversation summary (e.g., "Confirmed showing for Saturday")';
      case 'meeting':
        return 'Meeting topic (e.g., "Property tour - 123 Main St")';
      case 'note':
        return 'Note title (optional)';
      default:
        return 'Brief summary';
    }
  };

  const getContentLabel = () => {
    switch (formData.type) {
      case 'call':
        return formData.call_outcome === 'connected' ? 'Call Notes' : 'Notes (optional)';
      case 'email':
        return 'Email Summary';
      case 'sms':
        return 'Conversation Thread';
      case 'meeting':
        return 'Meeting Notes';
      case 'note':
        return 'Note Content';
      default:
        return 'Details';
    }
  };

  // Don't show direction for notes
  const showDirection = formData.type !== 'note';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditMode ? `Edit Interaction with ${contactName}` : `Log Interaction with ${contactName}`}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="px-6 pb-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Type Selection - Visual Buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Interaction Type
            </label>
            <div className="grid grid-cols-5 gap-2">
              {INTERACTION_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, type: type.value }))}
                  className={`flex flex-col items-center py-3 px-2 rounded-lg border-2 transition-all ${
                    formData.type === type.value
                      ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/30 text-ondeck-700 dark:text-ondeck-300'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <span className="text-xl mb-1">{type.icon}</span>
                  <span className="text-xs font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Direction - only for non-notes */}
          {showDirection && (
            <div className="grid grid-cols-2 gap-2">
              {DIRECTIONS.map((dir) => (
                <button
                  key={dir.value}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, direction: dir.value }))}
                  className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    formData.direction === dir.value
                      ? dir.value === 'outbound'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'border-red-400 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {dir.label}
                </button>
              ))}
            </div>
          )}

          {/* Call-specific fields */}
          {formData.type === 'call' && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <Select
                label="Call Outcome"
                options={CALL_OUTCOMES}
                value={formData.call_outcome}
                onChange={handleChange('call_outcome')}
              />
              {formData.call_outcome === 'connected' && (
                <Input
                  label="Call Duration (minutes)"
                  type="number"
                  min="1"
                  placeholder="e.g., 15"
                  value={formData.call_duration_minutes}
                  onChange={handleChange('call_duration_minutes')}
                />
              )}
            </div>
          )}

          {/* SMS Channel Selection */}
          {formData.type === 'sms' && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <Select
                label="Message Source"
                options={SMS_CHANNELS}
                value={formData.channel}
                onChange={handleChange('channel')}
              />
              <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                <strong>Tip:</strong> Copy your conversation from your messaging app and paste it below.
                Include both sides of the conversation for the best AI insights.
              </p>
            </div>
          )}

          {/* Email Channel Selection */}
          {formData.type === 'email' && (
            <Select
              label="Email Provider"
              options={EMAIL_CHANNELS}
              value={formData.channel}
              onChange={handleChange('channel')}
            />
          )}

          {/* Meeting-specific fields */}
          {formData.type === 'meeting' && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <Input
                label="Meeting Date"
                type="date"
                value={formData.meeting_date}
                onChange={handleChange('meeting_date')}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Start Time"
                  type="time"
                  value={formData.meeting_start_time}
                  onChange={handleChange('meeting_start_time')}
                />
                <Input
                  label="End Time"
                  type="time"
                  value={formData.meeting_end_time}
                  onChange={handleChange('meeting_end_time')}
                />
              </div>
              <Input
                label="Location"
                placeholder="e.g., 123 Main St, Zoom, Coffee shop"
                value={formData.meeting_location}
                onChange={handleChange('meeting_location')}
              />
            </div>
          )}

          {/* Subject */}
          <Input
            label="Summary"
            placeholder={getSubjectPlaceholder()}
            value={formData.subject}
            onChange={handleChange('subject')}
          />

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {getContentLabel()}
            </label>
            <textarea
              value={formData.content}
              onChange={handleChange('content')}
              rows={formData.type === 'sms' ? 8 : 4}
              className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500 text-sm ${
                formData.type === 'sms' ? 'font-mono' : ''
              }`}
              placeholder={getPlaceholder()}
            />
          </div>

          {/* When - for non-meeting types */}
          {formData.type !== 'meeting' && (
            <Input
              label="When did this happen?"
              type="datetime-local"
              value={formData.occurred_at}
              onChange={handleChange('occurred_at')}
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending} className="flex-1">
            {INTERACTION_TYPES.find(t => t.value === formData.type)?.icon} {isEditMode ? 'Update' : 'Log'} {INTERACTION_TYPES.find(t => t.value === formData.type)?.label}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
